import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { MultiSelect } from 'primereact/multiselect';
import reinsuranceMockService from '../../../../services/mockData/reinsuranceMockData';
import './style.scss';

const TreatyMaster = () => {
  const [treaties, setTreaties] = useState([]);
  const [reinsurers, setReinsurers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedTreaty, setSelectedTreaty] = useState(null);
  const [formData, setFormData] = useState({
    treatyNumber: '',
    name: '',
    type: null,
    lineOfBusiness: '',
    reinsurers: [],
    effectiveDate: null,
    expiryDate: null,
    retention: '',
    cession: {},
    commission: {}
  });
  const toast = useRef(null);

  const treatyTypes = [
    { label: 'Quota Share', value: 'Quota Share' },
    { label: 'Surplus', value: 'Surplus' },
    { label: 'Excess of Loss', value: 'Excess of Loss' },
    { label: 'Stop Loss', value: 'Stop Loss' },
    { label: 'NRCP Mandatory', value: 'NRCP Mandatory' }
  ];

  const linesOfBusiness = [
    { label: 'Motor', value: 'Motor' },
    { label: 'Fire', value: 'Fire' },
    { label: 'Property', value: 'Property' },
    { label: 'Marine', value: 'Marine' },
    { label: 'Personal Accident', value: 'Personal Accident' },
    { label: 'Health', value: 'Health' },
    { label: 'Engineering', value: 'Engineering' },
    { label: 'Catastrophe', value: 'Catastrophe' },
    { label: 'All Lines', value: 'All Lines' }
  ];

  const commissionTypes = [
    { label: 'Flat Rate', value: 'Flat' },
    { label: 'Sliding Scale', value: 'Sliding Scale' },
    { label: 'Profit Commission', value: 'Profit Commission' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [treatyData, reinsurerData] = await Promise.all([
        reinsuranceMockService.getTreaties(),
        reinsuranceMockService.getReinsurers()
      ]);
      setTreaties(treatyData);
      setReinsurers(reinsurerData);
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load data'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      treatyNumber: '',
      name: '',
      type: null,
      lineOfBusiness: '',
      reinsurers: [],
      effectiveDate: null,
      expiryDate: null,
      retention: '',
      cession: {},
      commission: { type: 'Flat', rate: '' }
    });
    setEditMode(false);
    setShowDialog(true);
  };

  const handleEdit = (treaty) => {
    setFormData({
      ...treaty,
      effectiveDate: new Date(treaty.effectiveDate),
      expiryDate: new Date(treaty.expiryDate)
    });
    setSelectedTreaty(treaty);
    setEditMode(true);
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editMode) {
        await reinsuranceMockService.updateTreaty(selectedTreaty.id, formData);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Treaty updated successfully'
        });
      } else {
        await reinsuranceMockService.addTreaty(formData);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Treaty created successfully'
        });
      }
      setShowDialog(false);
      loadData();
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save treaty'
      });
    }
  };

  const statusBodyTemplate = (rowData) => {
    const severity = rowData.status === 'Active' ? 'success' : 'warning';
    return <Tag value={rowData.status} severity={severity} />;
  };

  const utilizationBodyTemplate = (rowData) => {
    const color = rowData.utilization > 80 ? '#f44336' :
                  rowData.utilization > 60 ? '#ff9800' : '#4caf50';
    return (
      <div className="utilization-cell">
        <ProgressBar
          value={rowData.utilization}
          color={color}
          showValue={false}
          style={{ height: '6px' }}
        />
        <span className="utilization-text">{rowData.utilization}%</span>
      </div>
    );
  };

  const reinsurersBodyTemplate = (rowData) => {
    const reinsurerNames = rowData.reinsurers?.map(id => {
      const reinsurer = reinsurers.find(r => r.id === id);
      return reinsurer?.shortName || id;
    }).join(', ');
    return <span>{reinsurerNames}</span>;
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="action-buttons">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-primary"
          onClick={() => handleEdit(rowData)}
          tooltip="Edit Treaty"
        />
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-text"
          tooltip="View Details"
        />
      </div>
    );
  };

  const dialogFooter = (
    <div className="dialog-footer">
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setShowDialog(false)}
      />
      <Button
        label="Save"
        icon="pi pi-check"
        className="p-button-primary"
        onClick={handleSave}
      />
    </div>
  );

  const renderTreatyForm = () => {
    return (
      <div className="treaty-form">
        <TabView>
          <TabPanel header="Basic Information">
            <div className="p-fluid">
              <div className="p-field p-grid">
                <label className="p-col-12 p-md-3">Treaty Number*</label>
                <div className="p-col-12 p-md-9">
                  <InputText
                    value={formData.treatyNumber}
                    onChange={(e) => setFormData({...formData, treatyNumber: e.target.value})}
                    placeholder="e.g., QS-MOTOR-2025"
                  />
                </div>
              </div>

              <div className="p-field p-grid">
                <label className="p-col-12 p-md-3">Treaty Name*</label>
                <div className="p-col-12 p-md-9">
                  <InputText
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Enter treaty name"
                  />
                </div>
              </div>

              <div className="p-field p-grid">
                <label className="p-col-12 p-md-3">Treaty Type*</label>
                <div className="p-col-12 p-md-9">
                  <Dropdown
                    value={formData.type}
                    options={treatyTypes}
                    onChange={(e) => setFormData({...formData, type: e.value})}
                    placeholder="Select treaty type"
                  />
                </div>
              </div>

              <div className="p-field p-grid">
                <label className="p-col-12 p-md-3">Line of Business*</label>
                <div className="p-col-12 p-md-9">
                  <Dropdown
                    value={formData.lineOfBusiness}
                    options={linesOfBusiness}
                    onChange={(e) => setFormData({...formData, lineOfBusiness: e.value})}
                    placeholder="Select line of business"
                  />
                </div>
              </div>

              <div className="p-field p-grid">
                <label className="p-col-12 p-md-3">Reinsurers*</label>
                <div className="p-col-12 p-md-9">
                  <MultiSelect
                    value={formData.reinsurers}
                    options={reinsurers.map(r => ({
                      label: `${r.shortName} (${r.rating})`,
                      value: r.id
                    }))}
                    onChange={(e) => setFormData({...formData, reinsurers: e.value})}
                    placeholder="Select reinsurers"
                    display="chip"
                  />
                </div>
              </div>

              <div className="p-field p-grid">
                <label className="p-col-12 p-md-3">Effective Date*</label>
                <div className="p-col-12 p-md-9">
                  <Calendar
                    value={formData.effectiveDate}
                    onChange={(e) => setFormData({...formData, effectiveDate: e.value})}
                    dateFormat="dd/mm/yy"
                    placeholder="Select effective date"
                  />
                </div>
              </div>

              <div className="p-field p-grid">
                <label className="p-col-12 p-md-3">Expiry Date*</label>
                <div className="p-col-12 p-md-9">
                  <Calendar
                    value={formData.expiryDate}
                    onChange={(e) => setFormData({...formData, expiryDate: e.value})}
                    dateFormat="dd/mm/yy"
                    placeholder="Select expiry date"
                  />
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Coverage & Limits">
            <div className="p-fluid">
              {formData.type === 'Quota Share' && (
                <>
                  <div className="p-field p-grid">
                    <label className="p-col-12 p-md-3">Cession %</label>
                    <div className="p-col-12 p-md-9">
                      <InputNumber
                        value={formData.cession?.percentage}
                        onValueChange={(e) => setFormData({
                          ...formData,
                          cession: {...formData.cession, percentage: e.value}
                        })}
                        suffix="%"
                        min={0}
                        max={100}
                      />
                    </div>
                  </div>
                  <div className="p-field p-grid">
                    <label className="p-col-12 p-md-3">Retention (PHP)</label>
                    <div className="p-col-12 p-md-9">
                      <InputText
                        value={formData.retention}
                        onChange={(e) => setFormData({...formData, retention: e.target.value})}
                        placeholder="Enter retention amount"
                      />
                    </div>
                  </div>
                </>
              )}

              {formData.type === 'Surplus' && (
                <>
                  <div className="p-field p-grid">
                    <label className="p-col-12 p-md-3">Number of Lines</label>
                    <div className="p-col-12 p-md-9">
                      <InputNumber
                        value={formData.lines}
                        onValueChange={(e) => setFormData({...formData, lines: e.value})}
                        min={1}
                        max={20}
                      />
                    </div>
                  </div>
                  <div className="p-field p-grid">
                    <label className="p-col-12 p-md-3">Retention per Line (PHP)</label>
                    <div className="p-col-12 p-md-9">
                      <InputText
                        value={formData.retention}
                        onChange={(e) => setFormData({...formData, retention: e.target.value})}
                        placeholder="Enter retention amount"
                      />
                    </div>
                  </div>
                </>
              )}

              {formData.type === 'Excess of Loss' && (
                <>
                  <div className="p-field p-grid">
                    <label className="p-col-12 p-md-3">Layer Limit (PHP)</label>
                    <div className="p-col-12 p-md-9">
                      <InputText
                        value={formData.limit}
                        onChange={(e) => setFormData({...formData, limit: e.target.value})}
                        placeholder="Enter layer limit"
                      />
                    </div>
                  </div>
                  <div className="p-field p-grid">
                    <label className="p-col-12 p-md-3">Excess Point (PHP)</label>
                    <div className="p-col-12 p-md-9">
                      <InputText
                        value={formData.excess}
                        onChange={(e) => setFormData({...formData, excess: e.target.value})}
                        placeholder="Enter excess point"
                      />
                    </div>
                  </div>
                  <div className="p-field p-grid">
                    <label className="p-col-12 p-md-3">Reinstatements</label>
                    <div className="p-col-12 p-md-9">
                      <InputNumber
                        value={formData.reinstatements}
                        onValueChange={(e) => setFormData({...formData, reinstatements: e.value})}
                        min={0}
                        max={5}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabPanel>

          <TabPanel header="Commission">
            <div className="p-fluid">
              <div className="p-field p-grid">
                <label className="p-col-12 p-md-3">Commission Type</label>
                <div className="p-col-12 p-md-9">
                  <Dropdown
                    value={formData.commission?.type}
                    options={commissionTypes}
                    onChange={(e) => setFormData({
                      ...formData,
                      commission: {...formData.commission, type: e.value}
                    })}
                    placeholder="Select commission type"
                  />
                </div>
              </div>

              {formData.commission?.type === 'Flat' && (
                <div className="p-field p-grid">
                  <label className="p-col-12 p-md-3">Commission Rate</label>
                  <div className="p-col-12 p-md-9">
                    <InputNumber
                      value={parseFloat(formData.commission?.rate) || 0}
                      onValueChange={(e) => setFormData({
                        ...formData,
                        commission: {...formData.commission, rate: e.value + '%'}
                      })}
                      suffix="%"
                      min={0}
                      max={50}
                      minFractionDigits={1}
                      maxFractionDigits={2}
                    />
                  </div>
                </div>
              )}

              {formData.commission?.type === 'Sliding Scale' && (
                <div className="sliding-scale-section">
                  <h4>Loss Ratio Bands</h4>
                  <div className="p-field p-grid">
                    <label className="p-col-12 p-md-3">0-50%</label>
                    <div className="p-col-12 p-md-9">
                      <InputNumber
                        value={35}
                        suffix="%"
                        disabled
                      />
                    </div>
                  </div>
                  <div className="p-field p-grid">
                    <label className="p-col-12 p-md-3">50-60%</label>
                    <div className="p-col-12 p-md-9">
                      <InputNumber
                        value={32.5}
                        suffix="%"
                        disabled
                      />
                    </div>
                  </div>
                  <div className="p-field p-grid">
                    <label className="p-col-12 p-md-3">60-70%</label>
                    <div className="p-col-12 p-md-9">
                      <InputNumber
                        value={30}
                        suffix="%"
                        disabled
                      />
                    </div>
                  </div>
                  <div className="p-field p-grid">
                    <label className="p-col-12 p-md-3">70%+</label>
                    <div className="p-col-12 p-md-9">
                      <InputNumber
                        value={27.5}
                        suffix="%"
                        disabled
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabPanel>
        </TabView>
      </div>
    );
  };

  return (
    <div className="treaty-master">
      <Toast ref={toast} />

      <Card title="Reinsurance Treaty Master">
        <div className="header-actions">
          <Button
            label="Add Treaty"
            icon="pi pi-plus"
            className="p-button-primary"
            onClick={handleAdd}
          />
          <Button
            label="Export"
            icon="pi pi-download"
            className="p-button-secondary p-ml-2"
          />
        </div>

        <DataTable
          value={treaties}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
          className="treaty-table"
          emptyMessage="No treaties found"
          responsiveLayout="scroll"
        >
          <Column field="treatyNumber" header="Treaty Number" sortable />
          <Column field="name" header="Treaty Name" sortable />
          <Column field="type" header="Type" sortable />
          <Column field="lineOfBusiness" header="Line of Business" sortable />
          <Column header="Reinsurers" body={reinsurersBodyTemplate} />
          <Column field="effectiveDate" header="Effective Date" sortable />
          <Column field="expiryDate" header="Expiry Date" sortable />
          <Column header="Utilization" body={utilizationBodyTemplate} sortable />
          <Column header="Status" body={statusBodyTemplate} sortable />
          <Column header="Actions" body={actionBodyTemplate} />
        </DataTable>
      </Card>

      <Dialog
        visible={showDialog}
        onHide={() => setShowDialog(false)}
        header={editMode ? 'Edit Treaty' : 'Add New Treaty'}
        modal
        className="treaty-dialog"
        style={{ width: '70vw' }}
        footer={dialogFooter}
      >
        {renderTreatyForm()}
      </Dialog>
    </div>
  );
};

export default TreatyMaster;